import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t, getLang, setLang, SUPPORTED_LANGS } from '../../utils/creatorLang';
import { useCreatorAuth } from '../../utils/useCreatorAuth';
import AvatarImg from '../../components/AvatarImg';
import {
  CreatorScreen, Card, Pill, BottomNav,
  I, SectionLabel, TierBadge, DiamondGem,
} from '../../components/creator/primitives';

const SERVER_COLOR = { MENA: '#f97316', FR: '#3b82f6', IT: '#22c55e', DE: '#a855f7' };
const LANG_FLAGS = { en: '🇬🇧', fr: '🇫🇷', it: '🇮🇹', de: '🇩🇪' };

const fmtStat = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const CreatorProfile = () => {
  const navigate = useNavigate();
  const { tiktokId: authTiktokId, loading: authLoading, isDebugMode, logout } = useCreatorAuth();
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null);
  const [managerName, setManagerName] = useState('—');
  const [currentMonth, setCurrentMonth] = useState(null);
  const [lang, setLangState] = useState(getLang());

  useEffect(() => {
    if (authLoading || !authTiktokId) return;
    const init = async () => {
      setLoading(true);
      try {
        const { data: creatorData } = await supabase
          .from('black_creators')
          .select('tiktok_id, username, display_name, server, level, contract_start, contract_end, manager_id, tiktok_avatar_url, tiktok_display_name, tiktok_follower_count, tiktok_following_count, tiktok_likes_count, tiktok_video_count, whatsapp, agency_status')
          .eq('tiktok_id', authTiktokId)
          .single();

        if (!creatorData) { navigate('/my/login'); return; }
        setCreator(creatorData);

        if (creatorData.manager_id) {
          const { data: manager } = await supabase.from('profiles').select('name').eq('id', creatorData.manager_id).single();
          if (manager?.name) setManagerName(manager.name);
        }

        const { data: monthData } = await supabase
          .from('black_creator_current_month')
          .select('tier_current, tier_m1, is_active, diamonds')
          .eq('tiktok_id', authTiktokId)
          .single();
        setCurrentMonth(monthData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [authTiktokId, authLoading]);

  const handleLangChange = (l) => {
    setLang(l);
    setLangState(l);
  };

  const qs = isDebugMode && authTiktokId ? `?creator=${authTiktokId}` : '';

  if (loading) {
    return (
      <CreatorScreen>
        <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
          <div style={{
            width: 32, height: 32,
            border: '2px solid var(--accent-pink)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </CreatorScreen>
    );
  }
  if (!creator) return null;

  const initials = (creator.tiktok_display_name || creator.username || '??').slice(0, 2).toUpperCase();
  const tierCurrent = currentMonth?.tier_current;
  const tierM1 = currentMonth?.tier_m1;

  const contractStatus = (() => {
    if (creator.agency_status === 'left_agency') return { label: t('status_left'), color: 'red' };
    if (creator.contract_end && new Date(creator.contract_end) < new Date()) return { label: t('status_expired'), color: 'gold' };
    return { label: t('status_active'), color: 'green' };
  })();

  const Row = ({ label, value, color, isLink }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 0', borderBottom: '1px solid var(--line-1)',
    }}>
      <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{label}</span>
      {isLink ? (
        <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: color || 'var(--accent-green)', fontWeight: 600, textDecoration: 'none' }}>
          {value.replace('https://wa.me/', '+')}
        </a>
      ) : (
        <span className="mono" style={{ fontSize: 13, color: color || 'var(--fg-0)', fontWeight: 500 }}>{value}</span>
      )}
    </div>
  );

  return (
    <CreatorScreen>
      <div className="screen grain">
        {/* Hero */}
        <div style={{ padding: '48px 20px 20px', textAlign: 'center', position: 'relative' }}>
          <div style={{
            position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)',
            width: 260, height: 260, borderRadius: '50%',
            background: 'radial-gradient(circle, var(--accent-pink-soft) 0%, transparent 70%)',
            pointerEvents: 'none', zIndex: 0,
          }}/>
          <div style={{
            position: 'relative', zIndex: 1,
            width: 92, height: 92, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent-pink), var(--accent-gold))',
            margin: '0 auto', padding: 3,
            boxShadow: '0 8px 32px oklch(0.68 0.22 10 / 0.4)',
          }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              background: 'var(--bg-2)', overflow: 'hidden',
              display: 'grid', placeItems: 'center',
              color: 'var(--fg-0)', fontWeight: 800, fontSize: 28,
            }}>
              {creator.tiktok_avatar_url
                ? <AvatarImg src={creator.tiktok_avatar_url} username={creator.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                : initials}
            </div>
          </div>
          <div style={{ marginTop: 14, fontSize: 20, fontWeight: 700, color: 'var(--fg-0)', position: 'relative' }}>
            {creator.tiktok_display_name || creator.username}
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 2, position: 'relative' }}>
            @{creator.username}
          </div>
          <div style={{ marginTop: 12, display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', position: 'relative' }}>
            {tierCurrent && <Pill color="gold"><TierBadge tier={tierCurrent} size="sm"/> T{tierCurrent}</Pill>}
            {creator.server && (
              <Pill color="neutral" style={{ background: `${SERVER_COLOR[creator.server] || '#6b7280'}22`, color: SERVER_COLOR[creator.server] }}>
                {creator.server}
              </Pill>
            )}
            {creator.level && <Pill color="neutral">{creator.level}</Pill>}
            {currentMonth?.is_active != null && (
              <Pill color={currentMonth.is_active ? 'green' : 'red'}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: currentMonth.is_active ? 'var(--accent-green)' : 'var(--accent-red)' }}/>
                {currentMonth.is_active ? t('active') : t('inactive')}
              </Pill>
            )}
          </div>
        </div>

        {/* TikTok stats */}
        <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {[
            ['Followers', creator.tiktok_follower_count],
            ['Following', creator.tiktok_following_count],
            ['Likes', creator.tiktok_likes_count],
            ['Videos', creator.tiktok_video_count],
          ].map(([label, v]) => (
            <Card key={label} pad={12} style={{ textAlign: 'center' }}>
              <div className="mono tab" style={{ fontSize: 16, fontWeight: 700 }}>{fmtStat(v)}</div>
              <div style={{ fontSize: 9, color: 'var(--fg-3)', marginTop: 2 }}>{label}</div>
            </Card>
          ))}
        </div>

        {/* Information */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('information')}</SectionLabel>
          <Card pad={16} style={{ paddingTop: 4, paddingBottom: 0 }}>
            <Row label="TikTok ID" value={creator.tiktok_id}/>
            <Row label="Manager" value={managerName}/>
            <Row label={t('server')} value={creator.server || '—'} color={SERVER_COLOR[creator.server]}/>
            <Row label={t('level')} value={creator.level || '—'}/>
            <Row label={t('current_tier')} value={tierCurrent ? `T${tierCurrent}` : '—'} color="var(--accent-pink)"/>
            <Row label={t('tier_m1')} value={tierM1 ? `T${tierM1}` : '—'} color="var(--fg-3)"/>
            {creator.whatsapp ? (
              <Row label="WhatsApp" value={`https://wa.me/${creator.whatsapp.replace(/\D/g, '')}`} color="#25D366" isLink/>
            ) : (
              <Row label="WhatsApp" value={t('not_provided')} color="var(--fg-4)"/>
            )}
          </Card>
        </div>

        {/* Contract */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('agency_contract')}</SectionLabel>
          <Card pad={16} style={{ paddingTop: 4, paddingBottom: 4 }}>
            <Row label={t('contract_start')} value={formatDate(creator.contract_start)}/>
            <Row label={t('contract_end')} value={formatDate(creator.contract_end)}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{t('status')}</span>
              <Pill color={contractStatus.color}>{contractStatus.label}</Pill>
            </div>
          </Card>
        </div>

        {/* Language selector */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>Language</SectionLabel>
          <Card pad={12}>
            <div style={{ display: 'flex', gap: 8 }}>
              {SUPPORTED_LANGS.map(l => {
                const active = lang === l;
                return (
                  <div key={l} className="tap" onClick={() => handleLangChange(l)} style={{
                    flex: 1, padding: '10px 4px', borderRadius: 10,
                    background: active ? 'var(--accent-pink-soft)' : 'var(--bg-3)',
                    border: `1px solid ${active ? 'oklch(0.68 0.22 10 / 0.4)' : 'var(--line-1)'}`,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 20 }}>{LANG_FLAGS[l]}</div>
                    <div style={{ fontSize: 9, color: active ? 'var(--accent-pink)' : 'var(--fg-3)', marginTop: 2, fontWeight: active ? 700 : 500, letterSpacing: 0.3 }}>
                      {l.toUpperCase()}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Quick links to other pages */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>{t('quick_links')}</SectionLabel>
          <Card pad={0}>
            {[
              { icon: <I.Shield size={16} stroke="var(--accent-gold)"/>, tint: 'gold', label: t('cashback_rules'), to: `/my/rules${qs}` },
              { icon: <I.Book size={16} stroke="var(--accent-cyan)"/>, tint: 'cyan', label: 'Community Guidelines', to: `/my/guidelines${qs}` },
              { icon: <I.Sword size={16} stroke="var(--accent-pink)"/>, tint: 'pink', label: t('match_request'), to: `/my/match${qs}` },
            ].map((item, i, arr) => (
              <Link key={i} to={item.to} style={{ textDecoration: 'none' }}>
                <div className="tap" style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                  borderBottom: i < arr.length - 1 ? '1px solid var(--line-1)' : 'none',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 9,
                    background: `var(--accent-${item.tint}-soft)`,
                    display: 'grid', placeItems: 'center',
                  }}>{item.icon}</div>
                  <span style={{ flex: 1, fontSize: 14, color: 'var(--fg-0)' }}>{item.label}</span>
                  <I.Chevron size={14} stroke="var(--fg-3)"/>
                </div>
              </Link>
            ))}
          </Card>
        </div>

        {/* Legal */}
        <div style={{
          padding: '20px 20px 0', display: 'flex',
          justifyContent: 'center', gap: 12, flexWrap: 'wrap',
          fontSize: 11, color: 'var(--fg-3)',
        }}>
          <Link to="/terms" style={{ color: 'var(--fg-3)', textDecoration: 'none' }}>Terms</Link>
          <span style={{ color: 'var(--fg-4)' }}>·</span>
          <Link to="/privacy" style={{ color: 'var(--fg-3)', textDecoration: 'none' }}>Privacy</Link>
        </div>

        {/* Logout */}
        <div style={{ padding: '16px 20px 0' }}>
          <div className="tap" onClick={logout} style={{
            padding: 14, borderRadius: 16,
            background: 'var(--accent-red-soft)',
            border: '1px solid oklch(0.65 0.24 25 / 0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            color: 'var(--accent-red)', fontSize: 13, fontWeight: 600,
          }}>
            <I.Logout size={16} stroke="var(--accent-red)"/>
            {t('logout')}
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--fg-4)', padding: '16px 20px 0' }}>
          NoiRock v2.5 · NoiRock
        </div>
      </div>

      <BottomNav active="profile" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorProfile;
